import { useLayoutEffect, useRef } from 'react';
import {
    GRADIENT_COLOR_BOTTOM,
    GRADIENT_COLOR_TOP,
    isNonMobileWeb,
} from 'smoothie';

const DEFAULT_VIEWPORT =
    'width=device-width, initial-scale=1, shrink-to-fit=no';
const COVER_VIEWPORT = `${DEFAULT_VIEWPORT}, viewport-fit=cover`;
const MOBILE_WEB_HTML_GRADIENT = `linear-gradient(to bottom, ${GRADIENT_COLOR_TOP}, ${GRADIENT_COLOR_BOTTOM})`;

/**
 * Extends the app's gradient under the notch/home-indicator on mobile web.
 * The default viewport meta (+html.tsx) omits viewport-fit=cover, so that
 * space shows native WebView chrome instead of page content — no in-page
 * element or background-color can reach it, only <html>/<body> can. Pinning
 * <body> to a measured pixel height (rather than leaving it in normal flow)
 * turned out to matter for more than keyboard-avoidance — removing it
 * reintroduced a white gap wherever content scrolls past one viewport, so
 * it's staying. Port of CMM's useMobileWebChatLock, minus its keyboard/
 * gesture-prevention handling.
 *
 * Callers still need: a React-rendered background (e.g. BackgroundGradient)
 * as a baseline, since this effect's DOM mutations aren't guaranteed before
 * first paint on every navigation path; and, if inside a Stack, that route's
 * `contentStyle` set to transparent in `_layout.tsx`, since the navigator's
 * own opaque background sits above this effect.
 */
export const useFullBleedMobileWebBackground = () => {
    const initialViewportHeightRef = useRef(
        typeof window !== 'undefined' ? window.innerHeight : 0,
    );

    useLayoutEffect(() => {
        if (isNonMobileWeb || typeof document === 'undefined') {
            return undefined;
        }

        const viewportMetas = document.querySelectorAll(
            'meta[name="viewport"]',
        );
        viewportMetas.forEach((meta) => {
            meta.setAttribute('content', COVER_VIEWPORT);
        });

        const html = document.documentElement;
        const body = document.body;
        const root = document.getElementById('root');
        const prev = {
            htmlMinHeight: html.style.minHeight,
            htmlBgImage: html.style.backgroundImage,
            htmlBgAttachment: html.style.backgroundAttachment,
            htmlBgColor: html.style.backgroundColor,
            bodyHeight: body.style.height,
            bodyOverflow: body.style.overflow,
            bodyPosition: body.style.position,
            bodyWidth: body.style.width,
            bodyTop: body.style.top,
            bodyLeft: body.style.left,
            bodyBgColor: body.style.backgroundColor,
            rootBgColor: root?.style.backgroundColor ?? '',
        };

        html.style.minHeight = '100lvh';
        html.style.backgroundImage = MOBILE_WEB_HTML_GRADIENT;
        html.style.backgroundAttachment = 'fixed';
        html.style.backgroundColor = GRADIENT_COLOR_BOTTOM;
        body.style.height = `${initialViewportHeightRef.current.toString()}px`;
        body.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.width = '100%';
        body.style.top = '0';
        body.style.left = '0';
        body.style.backgroundColor = 'transparent';
        if (root != null) {
            root.style.backgroundColor = 'transparent';
        }

        return () => {
            viewportMetas.forEach((meta) => {
                meta.setAttribute('content', DEFAULT_VIEWPORT);
            });
            html.style.minHeight = prev.htmlMinHeight;
            html.style.backgroundImage = prev.htmlBgImage;
            html.style.backgroundAttachment = prev.htmlBgAttachment;
            html.style.backgroundColor = prev.htmlBgColor;
            body.style.height = prev.bodyHeight;
            body.style.overflow = prev.bodyOverflow;
            body.style.position = prev.bodyPosition;
            body.style.width = prev.bodyWidth;
            body.style.top = prev.bodyTop;
            body.style.left = prev.bodyLeft;
            body.style.backgroundColor = prev.bodyBgColor;
            if (root != null) {
                root.style.backgroundColor = prev.rootBgColor;
            }
        };
    }, []);
};
